/**
 * 数値・金額・日付の表示フォーマット。
 * 画面では必ずこのモジュール経由で整形し、表記ゆれを防ぐ。
 */
import { toWareki, toYMD } from './wareki';

const nf0 = new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 });

/** 3 桁カンマ。null/undefined/NaN は空文字 */
export function formatNumber(value: number | string | null | undefined, fractionDigits = 0): string {
  if (value == null || value === '') return '';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '';
  if (fractionDigits === 0) return nf0.format(n);
  return new Intl.NumberFormat('ja-JP', {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  }).format(n);
}

/** 金額（円）。`formatYen(12000)` → `12,000円` / `{ symbol: true }` → `¥12,000` */
export function formatYen(value: number | string | null | undefined, opts: { symbol?: boolean } = {}): string {
  const s = formatNumber(value);
  if (s === '') return '';
  return opts.symbol ? `¥${s}` : `${s}円`;
}

/** パーセント（小数 0〜1 ではなく % 値を受け取る）。`formatPercent(110)` → `110%` */
export function formatPercent(value: number | null | undefined, fractionDigits = 0): string {
  if (value == null || !Number.isFinite(value)) return '';
  return `${formatNumber(value, fractionDigits)}%`;
}

/** カンマ・全角数字・円記号を含む文字列を数値へ。空や不正は null */
export function parseNumber(text: string): number | null {
  const normalized = text
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/[，,円¥\s]/g, '')
    .replace(/[－ー−]/g, '-')
    .replace(/．/g, '.');
  if (normalized === '' || normalized === '-') return null;
  if (!/^-?\d*\.?\d*$/.test(normalized)) return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/** `2026-04-01` → `2026/04/01` */
export function formatDate(value: string | Date | null | undefined): string {
  const ymd = toYMD(value ?? null);
  if (!ymd) return '';
  return `${ymd.y}/${String(ymd.m).padStart(2, '0')}/${String(ymd.d).padStart(2, '0')}`;
}

/** `2026-04-01` → `2026/04/01（令和8年4月1日）` */
export function formatDateWithWareki(value: string | Date | null | undefined): string {
  const s = formatDate(value);
  if (!s) return '';
  return `${s}（${toWareki(value ?? null)}）`;
}

/** ISO 日時 → `2026/04/01 09:30` */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
